import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useBrandingStore } from '@/stores/brandingStore';
import { ApiError } from '@/lib/api';
import { useOidcPasswordGuard } from './useOidcPasswordGuard';

const API_BASE_URL = '/api/v1';

type PageState = 'form' | 'success';

async function fetchPublic(endpoint: string, options?: RequestInit): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data);
  }
}

export default function ForgotPasswordPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const brandedName = useBrandingStore((s) => s.branding?.branding?.companyName);
  const { blocked } = useOidcPasswordGuard();

  const [pageState, setPageState] = useState<PageState>('form');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      await fetchPublic('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      // 200 is success even for an unknown address — the API already hides
      // whether the email exists. Only a failed request is an error here.
      setPageState('success');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? t('resetPassword.unknownTenant')
          : t('resetPassword.requestFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 440, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            color="primary"
            textAlign="center"
            gutterBottom
          >
            {brandedName || t('appName')}
          </Typography>

          {blocked && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!blocked && pageState === 'form' && (
            <>
              <Typography
                variant="h6"
                textAlign="center"
                gutterBottom
              >
                {t('resetPassword.title')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mb: 3 }}
              >
                {t('resetPassword.subtitle')}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={(e) => void handleSubmit(e)}>
                <TextField
                  label={t('resetPassword.emailLabel')}
                  type="email"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={submitting || !email.trim()}
                >
                  {submitting ? <CircularProgress size={24} /> : t('resetPassword.sendLink')}
                </Button>
              </form>

              <Button
                startIcon={<ArrowBackIcon />}
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate('/login')}
              >
                {t('resetPassword.backToLogin')}
              </Button>
            </>
          )}

          {!blocked && pageState === 'success' && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('resetPassword.emailSent')}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('resetPassword.emailSentDesc')}
              </Typography>
              <Button
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                onClick={() => navigate('/login')}
              >
                {t('resetPassword.backToLogin')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
